// @ts-strict-ignore
import upperFirst from 'lodash/upperFirst';
import startCase from 'lodash/startCase';

import { convertRuleToString, convertThresholdToString } from 'flow/service/thresholds';
import { convertExpressionToString } from 'flow/service/actions/outputAttributes';
import { convertMLAToString } from 'flow/service/mla';
import type { MatrixDimensions, CustomList } from 'flow/service/actions';
import {
  getActionTypeText,
  getActionName,
  isActionAnImportedValue,
  getFormattedMatrixDataType,
  getCommaSeparatedMatrixValues,
  getFormattedMatrixCellModifications,
  isActionOfType,
} from 'flow/service/actions';
import type {
  Action, DataRoute, Expression, ServicesByName, Threshold, MatrixModel,
  OutputValueRange,
} from '~/dashboard/views/workflows/store/engine/types';
import type { SdkConfigFieldBase } from '~/dashboard/views/workflows/editor/components/MenuBar/MenuBarModals/ThreeDotsModals/SDKConfigModal/types';
import type { WorkflowState } from '~/dashboard/views/workflows/store/redux/state';
import type { FeatureFlags } from '~/helpers/typescript';

import { Operation } from '../../types';
import { getOperationVerbInPastTense } from '../helpers';
import type { SUPPORTED_INPUT_METADATA_FIELDS, SUPPORTED_OUTPUT_METADATA_FIELDS, SUPPORTED_OUTPUT_OPTION_FIELDS } from '../../constants';
import type { TemplateAtoms } from './types';
import type { MatrixCellChangeAddedDeleted, MatrixCellChangeModified } from '../types';
import { ActionType } from 'flow/store/engine/constants';

export type Value<T = unknown> = boolean | number | string | number[] | string[] | T;
export type ModifyDirection = 'from' | 'to';
type AsAtom = keyof TemplateAtoms<unknown>;
type FormattingOptions = {
  doubleQuotes?: boolean;
};

function parseValue(value: Value): string {
  if (typeof value === 'boolean' && !value) {
    return 'false';
  }

  if (!value) {
    return 'none';
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return 'none';
    }

    return value.join(', ');
  }


  return String(value);
}


export default function createTemplates<T>({
  atoms, featureFlags,
}: {
  atoms: TemplateAtoms<T>;
  featureFlags: Partial<FeatureFlags>;
}) {
  const templateUtils = {
    emptyLine() {
      return atoms.emptyLine();
    },
    subtitle(scope: string, operation: Operation) {
      const opInPastTense = getOperationVerbInPastTense(operation);
      return atoms.heading2(`${scope} ${upperFirst(opInPastTense)}`);
    },
    from(args: {
      value: Value;
      formattingOptions?: FormattingOptions;
    }) {
      const { value, formattingOptions: { doubleQuotes = true } = {} } = args;
      const formattedValue = parseValue(value);
      return doubleQuotes
        ? atoms.text(`From: "${formattedValue}"`)
        : atoms.text(`From: ${formattedValue}`);
    },
    to(args: {
      value: Value;
      formattingOptions?: FormattingOptions;
    }) {
      const { value, formattingOptions: { doubleQuotes = true } = {} } = args;
      const formattedValue = parseValue(value);

      return doubleQuotes
        ? atoms.text(`To: "${formattedValue}"`)
        : atoms.text(`To: ${formattedValue}`);
    },
    fromTo(args: {
      field?: string;
      next: Value;
      prev: Value;
      asAtom?: AsAtom;
    }) {
      const {
        field,
        next,
        prev,
        asAtom = 'text',
      } = args;

      if (!field) {
        return atoms[asAtom](`From "${prev}" to "${next}"`);
      }
      return atoms[asAtom](`Modified ${field} from "${prev}" to "${next}"`);
    },
  };


  /**
   * the return type of this object becomes the interface of this module
   * if the need to create another implementation of this same interface comes,
   * move the function signatures to an `interface` and have this object implement it
   */
  return {
    emptyLine() {
      return templateUtils.emptyLine();
    },
    errorLine() {
      return atoms.error('Error computing changelog entry. Changes will still be saved.');
    },
    actionSectionTitle(args: {
      action: Action;
      operation: Operation;
    }) {
      const { action, operation } = args;

      const actionTypeText = getActionTypeText(action);
      const actionName = isActionOfType(action, ActionType.EXIT)
        ? 'Exit Workflow'
        : getActionName(action);

      const operationSuffix = (
        !isActionAnImportedValue(action)
          ? upperFirst(getOperationVerbInPastTense(operation))
          // imported values titles are formatted slightly different
          : {
            [Operation.Add]: 'Value',
            [Operation.Remove]: 'Value Deleted',
            [Operation.Modify]: 'Value Modified',
          }[operation]
      );

      return atoms.heading1(`${actionTypeText} ${operationSuffix}: ${actionName}`);
    },
    actionRulesGroupingModifiedSubtitle() {
      return atoms.heading2('Grouping, AND/OR Logic Modified');
    },
    actionRulesAsTextValue(args: {
      action: Action;
      direction?: ModifyDirection;
      workflowData: WorkflowState['workflowData'];
      workflowProps: WorkflowState['workflowProps'];
      formattingOptions?: FormattingOptions;
    }) {
      const {
        action,
        direction,
        workflowData: {
          actions,
          thresholds,
        },
        workflowProps: {
          reasonCodeGroups,
          services,
        },
        formattingOptions: {
          // rules should always be rendered without quotes, unless opted in
          doubleQuotes = false,
        } = {},
      } = args;

      const { rules } = action;

      const rulesAsString = convertRuleToString({
        actions,
        reasonCodeGroups,
        rules,
        services,
        thresholds,
        featureFlags,
      });

      if (!direction) {
        return atoms.text(rulesAsString);
      }

      const templateBuilder = direction === 'from' ? templateUtils.from : templateUtils.to;

      return templateBuilder({
        value: rulesAsString,
        formattingOptions: { doubleQuotes },
      });
    },
    thresholdOperationSubtitle(args: {
      operation: Operation;
    }) {
      const { operation } = args;
      return templateUtils.subtitle('Threshold', operation);
    },
    thresholdAsTextValue(args: {
      threshold: Threshold;
      version: WorkflowState['workflowData'];
      workflowProps: WorkflowState['workflowProps'];
    }) {
      const {
        threshold, version, workflowProps,
      } = args;
      const thresholdAsString = convertThresholdToString({
        actions: version.actions,
        threshold: threshold as Threshold,
        reasonCodeGroups: workflowProps.reasonCodeGroups,
        services: workflowProps.services,
        featureFlags,
      });

      return atoms.text(thresholdAsString);
    },
    thresholdModifiedTextValue(
      args: {
        threshold: Threshold;
        version: WorkflowState['workflowData'];
        workflowProps: WorkflowState['workflowProps'];
        direction: ModifyDirection;
      }) {
      const {
        direction,
        threshold,
        version,
        workflowProps,
      } = args;

      const thresholdAsString = convertThresholdToString({
        actions: version.actions,
        threshold: threshold as Threshold,
        reasonCodeGroups: workflowProps.reasonCodeGroups,
        services: workflowProps.services,
        featureFlags,
      });

      const templateBuilder = direction === 'from' ? templateUtils.from : templateUtils.to;

      return templateBuilder({ value: thresholdAsString });
    },
    mlaOperationSubtitle(args: {
      operation: Operation;
    }) {
      const { operation } = args;
      return templateUtils.subtitle('Multi-Level-Attribute', operation);
    },
    mlaAsTextValue(args: {
      actions: Record<string, Action>;
      services: ServicesByName;
      sourceAction: Action;
      customLists: CustomList[];
      mlaDataBundle:
      {
        source: 'threshold';
        thresholds: Record<string, Threshold>;
        threshold: Threshold;
      } | {
        source: 'dataroute';
        dataRoutes: Record<string, DataRoute>;
        dataRoute: DataRoute;
      };
    }) {
      const {
        mlaDataBundle, actions, services, sourceAction, customLists,
      } = args;
      const mlaAsString = convertMLAToString({
        actions,
        mlaDataBundle,
        services,
        sourceAction,
        customLists,
      });
      return atoms.text(mlaAsString);
    },
    mlaModifiedValue(args: {
      actions: Record<string, Action>;
      direction: ModifyDirection;
      services: ServicesByName;
      sourceAction: Action;
      customLists: CustomList[];
      mlaDataBundle:
      {
        source: 'threshold';
        thresholds: Record<string, Threshold>;
        threshold: Threshold;
      } | {
        source: 'dataroute';
        dataRoutes: Record<string, DataRoute>;
        dataRoute: DataRoute;
      };
    }) {
      const {
        direction, actions, services, sourceAction, mlaDataBundle, customLists,
      } = args;
      const mlaAsString = convertMLAToString({
        actions,
        services,
        sourceAction,
        mlaDataBundle,
        customLists,
      });

      const templateBuilder = direction === 'from' ? templateUtils.from : templateUtils.to;
      return templateBuilder({ value: mlaAsString });
    },
    inputActionMetadataValueNameSubtitle(args: {
      field: (typeof SUPPORTED_INPUT_METADATA_FIELDS)[number] | string;
      op: Operation;
    }) {
      const { field, op } = args;
      const formattedFieldMap: Record<typeof args['field'], string> = {
        dataType: 'Data Type',
        jqFunction: 'JQ',
        name: 'Display Name',
      };
      const formattedField = formattedFieldMap[field] ?? field;

      return templateUtils.subtitle(formattedField, op);
    },
    outputActionMetadataValueSubtitle(args: {
      field: (typeof SUPPORTED_OUTPUT_METADATA_FIELDS)[number] | string;
      op: Operation;
    }) {
      const { field, op } = args;
      const formattedFieldMap: Record<typeof args['field'], string> = {
        dataType: 'Data Type',
        jqFunction: 'JQ',
        name: 'Display Name',
        useJq: 'Use JQ',
        output_value_range: 'Value Ranges',
        category: 'Category',
      };
      const formattedField = formattedFieldMap[field] ?? field;

      return templateUtils.subtitle(formattedField, op);
    },
    outputActionOptionsValueSubtitle(args: {
      field: (typeof SUPPORTED_OUTPUT_OPTION_FIELDS)[number] | string;
      op: Operation;
    }) {
      const { field, op } = args;
      const formattedFieldMap: Record<typeof args['field'], string> = {
        output_value_range: 'Value Ranges',
        category: 'Category',
      };
      const formattedField = formattedFieldMap[field] ?? field;

      return templateUtils.subtitle(formattedField, op);
    },
    outputValueRangeRowSubtitle(args: {
      row?: number;
      op: Operation;
    }) {
      const { row, op } = args;
      if (typeof row === 'number' && op === Operation.Modify) {
        return templateUtils.subtitle(`Value Range Row ${row}`, op);
      }
      if (op === Operation.Remove) {
        return atoms.heading2('Value Range Rows Removed');
      }
      // default case Operation.Add
      return atoms.heading2('Value Range Rows Added');
    },
    outputValueRangeValueModified(args: {
      value: OutputValueRange;
      comparisonValue: OutputValueRange;
      comparisonSide: 'To' | 'From';
    }) {
      const {
        value: {
          color,
          label,
          maxValue,
          minValue,
        },
        comparisonValue: {
          color: comparisonColor,
          label: comparisonLabel,
          maxValue: comparisonMax,
          minValue: comparisonMin,
        },
        comparisonSide,
      } = args;
      const valueTexts = [];
      if (label !== comparisonLabel) {
        valueTexts.push(`Label: "${label}"`);
      }
      if (
        minValue !== comparisonMin
        || maxValue !== comparisonMax
      ) {
        valueTexts.push(`Range: ${minValue} - ${maxValue}`);
      }
      if (color !== comparisonColor) {
        valueTexts.push(`Color: ${color ? `#${color}` : 'None'}`);
      }
      return atoms.text(
        `${comparisonSide}: ${valueTexts.join(', ')}`,
      );
    },
    outputValueRangeValueAdded(args: {
      row: number;
      value: OutputValueRange;
    }) {
      const {
        value: {
          color,
          label,
          maxValue,
          minValue,
        },
        row,
      } = args;
      return atoms.text(
        `Range Row ${row}: Label: "${label}", Range: ${minValue} - ${maxValue}, Color: ${color ? `#${color}` : 'None'}`,
      );
    },
    outputValueRangeValueRemoved(args: {
      rows: number[];
    }) {
      const { rows } = args;
      return atoms.text(`Range Rows ${rows.join(', ')}`);
    },
    outputValueRangeRemoved() {
      return templateUtils.subtitle('Value Range', Operation.Remove);
    },
    outputExpressionModifiedSubtitle() {
      return templateUtils.subtitle('Output Expression', Operation.Modify);
    },
    outputExpressionModifiedValue(args: {
      actions: WorkflowState['workflowData']['actions'];
      dataRoutes: WorkflowState['workflowData']['dataRoutes'];
      expression: Expression;
      direction: ModifyDirection;
    }) {
      const { direction, ...rest } = args;

      const expressionAsString = convertExpressionToString(rest);
      const templateBuilder = direction === 'from' ? templateUtils.from : templateUtils.to;

      return templateBuilder({
        value: expressionAsString,
        formattingOptions: { doubleQuotes: false },
      });
    },
    attributeActionMetadataValue(args: {
      value: string;
      format?: 'start-case';
    }) {
      const { value, format } = args;

      const formatterMap: Record<typeof args['format'], Function> = {
        'start-case': startCase,
      };

      const formatter = formatterMap[format];
      const formattedValue = formatter?.(value) ?? value;

      return atoms.text(formattedValue);
    },
    attributeMetadataModifiedValue(args: {
      prev: Value;
      next: Value;
    }) {
      const { prev, next } = args;
      return templateUtils.fromTo({
        next,
        prev,
      });
    },
    publishedAttributeNewSubtitle() {
      return atoms.heading2('Published Attribute');
    },
    publishedAttributeExistingSubtitle() {
      return atoms.heading2('Existing Published Attribute');
    },
    publishedAttributePublishValue(args: {
      field: string;
      value: Value;
    }) {
      const {
        field,
        value,
      } = args;

      const parsedValue = parseValue(value);
      const formattedField = startCase(field);

      return atoms.text(`Attribute was published with a ${formattedField} of "${parsedValue}"`);
    },
    publishedAttributeExistingValue(args: {
      field: string;
      value: Value;
    }) {
      const {
        field,
        value,
      } = args;

      const parsedValue = parseValue(value);
      const formattedField = startCase(field);

      return atoms.text(`Attribute has a ${formattedField} of "${parsedValue}"`);
    },
    publishedAttributeModifiedValue(args: {
      prev: Value;
      next: Value;
      field: string;
    }) {
      const {
        field, next, prev,
      } = args;

      return templateUtils.fromTo({
        next: parseValue(next),
        prev: parseValue(prev),
        asAtom: 'bulletPoint',
        field,
      });
    },
    outcomeDecisionToDefault(args: {
      outcomeAction: Action;
    }) {
      const { outcomeAction } = args;
      const actionName = getActionName(outcomeAction);
      return atoms.text(`Else all Evaluations will be decisioned to ${actionName}`);
    },
    outcomePrecedenceModifiedSubtitle() {
      return atoms.heading2('Precedence Changed');
    },
    outcomePrecedenceModifiedValue(args: {
      prev: number;
      next: number;
    }) {
      return templateUtils.fromTo({
        ...args,
        field: 'precedence',
      });
    },
    workflowRoutingRunDefault(args: {
      workflowRoutingAction: Action;
    }) {
      const { workflowRoutingAction } = args;
      const actionName = getActionName(workflowRoutingAction);
      return atoms.text(`Else all Evaluations will be run through ${actionName}`);
    },
    workflowRoutingPrecedenceModifiedSubtitle() {
      return atoms.heading2('Precedence Changed');
    },
    workflowRoutingPrecedenceModifiedValue(args: {
      prev: number;
      next: number;
    }) {
      return templateUtils.fromTo({
        ...args,
        field: 'precedence',
      });
    },
    serviceAlwaysRunsText(args: {
      serviceAction: Action;
    }) {
      const { serviceAction } = args;
      const actionName = getActionName(serviceAction);
      return atoms.text(`Service ${actionName} always runs`);
    },
    serviceMetadataCredentialsSubtitle(args: { operation: Operation }) {
      const { operation } = args;
      return templateUtils.subtitle('Credentials', operation);
    },
    serviceMetadataOwnersSubtitle(args: { operation: Operation }) {
      const { operation } = args;
      return templateUtils.subtitle('Owner Field', operation);
    },
    serviceMetadataKeyValuesSubtitle(
      args: { operation: Operation; enabledServiceConfig?: boolean },
    ) {
      const { operation, enabledServiceConfig } = args;
      if (enabledServiceConfig) {
        return templateUtils.subtitle('Service Configuration', operation);
      }
      return templateUtils.subtitle('Key/Value', operation);
    },
    serviceMetadataRecurringWorkflowSubtitle(args: { operation: Operation }) {
      const { operation } = args;
      return templateUtils.subtitle('Recurring Webhook Workflow', operation);
    },
    serviceMetadataCredentialOwnerSubtitle(args: { operation: Operation }) {
      const { operation } = args;
      return templateUtils.subtitle('Credential Owner', operation);
    },
    serviceMetadataRecurringWorkflowContent(args: {
      name: string;
      op: Operation;
      prev?: Value;
      next?: Value;
      isSecret?: boolean;
    }) {
      const {
        name, op, prev, next, isSecret = false,
      } = args;
      const opInPastTenseCapitalized = upperFirst(getOperationVerbInPastTense(op));
      if (isSecret) {
        // we don't want to show the value of secrets, only their name
        return atoms.text(`${opInPastTenseCapitalized} "${name}"`);
      }
      const template = {
        [Operation.Add]: `Added Workflow "${parseValue(next)}"`,
        [Operation.Modify]: `From "${parseValue(prev)}" To "${parseValue(next)}"`,
        [Operation.Remove]: `Deleted Workflow "${parseValue(prev)}"`,
      }[op];

      return atoms.text(template);
    },
    serviceMetadataValueModified(args: {
      name: string;
      op: Operation;
      prev?: Value;
      next?: Value;
      isSecret?: boolean;
      isOwner?: boolean;
      enabledServiceConfig?: boolean;
    }) {
      const {
        name, op, prev, next, isSecret = false, isOwner = false, enabledServiceConfig = false,
      } = args;
      const opInPastTenseCapitalized = upperFirst(getOperationVerbInPastTense(op));

      if (isSecret || isOwner) {
        // we don't want to show the value of secrets, only their name
        return atoms.bulletPoint(`${opInPastTenseCapitalized} "${name}"`);
      }

      const parsedPrev = parseValue(prev);
      const parsedNext = parseValue(next);

      const template = {
        [Operation.Add]: `with value "${parsedNext}"`,
        [Operation.Remove]: `with value "${parsedPrev}"`,
        [Operation.Modify]: `from "${parsedPrev}" to "${parsedNext}"`,
      }[op];
      if (enabledServiceConfig) {
        return atoms.bulletPoint(`${opInPastTenseCapitalized} workflow level "${name}" ${template}`);
      }

      return atoms.bulletPoint(`${opInPastTenseCapitalized} "${name}" ${template}`);
    },
    tagGlobalSettingModifiedSubtitle() {
      return templateUtils.subtitle('Global Settings', Operation.Modify);
    },
    tagGlobalSettingsModifiedValue(args: {
      field: string;
      prev: Value;
      next: Value;
    }) {
      const { field, next, prev } = args;

      return templateUtils.fromTo({
        next: parseValue(next),
        prev: parseValue(prev),
        field: startCase(field),
      });
    },
    tagLocalSettingModifiedSubtitle() {
      return templateUtils.subtitle('Local Settings', Operation.Modify);
    },
    tagLocalSettingModifiedServiceFailureDefault(args: {
      value: boolean;
      direction: ModifyDirection;
    }) {
      const { value, direction } = args;

      // using a Map so we can index on `null` consistently
      const valueMap = new Map();
      valueMap.set(null, 'Exit Workflow');
      valueMap.set(false, 'Do Not Apply Tag');
      valueMap.set(true, 'Apply Tag');


      const formattedValue = valueMap.get(value);

      return direction === 'from'
        ? templateUtils.from({ value: formattedValue })
        : templateUtils.to({ value: formattedValue });
    },
    matrixMetadataSubtitle(args: {
      operation: Operation;
      field: string;
    }) {
      const { field, operation } = args;
      const formattedField = startCase(field);

      return templateUtils.subtitle(formattedField, operation);
    },
    matrixDataTypeValue(args: {
      dataType: MatrixModel['matrixDataType'];
    }) {
      const { dataType } = args;
      const formattedValue = startCase(getFormattedMatrixDataType(dataType));
      return atoms.text(formattedValue);
    },
    matrixDimensionsValue(args: {
      dimensions: MatrixDimensions;
    }) {
      const { dimensions } = args;
      const formattedValue = dimensions === 1
        ? '1D (Y Axis)'
        : '2D (X and Y Axis)';
      return atoms.text(formattedValue);
    },
    matrixCellValues(args: {
      matrix: MatrixModel['matrix'];
    }) {
      const { matrix } = args;
      const formattedValue = getCommaSeparatedMatrixValues(matrix);
      return atoms.text(formattedValue);
    },
    matrixCellAddedDeletedValue(args: {
      changesAddedDeleted: MatrixCellChangeAddedDeleted;
    }) {
      const { changesAddedDeleted } = args;
      const formattedValue = getFormattedMatrixCellModifications(changesAddedDeleted);
      return atoms.text(formattedValue);
    },
    matrixMetadataModifiedValue(args: {
      direction: ModifyDirection;
      value: Value;
      field: string;
    }) {
      const { direction, field, value } = args;

      let formattedValue = null;
      switch (field) {
        case 'dimensions':
          formattedValue = value === 1 ? '1D (Y Axis)' : '2D (X and Y Axis)';
          break;
        case 'dataType':
          formattedValue = getFormattedMatrixDataType(value as string);
          break;
        case 'cellChanges':
          formattedValue = getFormattedMatrixCellModifications(
            value as MatrixCellChangeModified,
            direction,
          );
          break;
        default:
          formattedValue = value;
      }

      const templateUtilArgs = {
        value: formattedValue,
        formattingOptions: {
          doubleQuotes: field !== 'cellChanges',
        },
      };

      return direction === 'from'
        ? templateUtils.from(templateUtilArgs)
        : templateUtils.to(templateUtilArgs);
    },
    matrixMetadataConfigurationModified() {
      return atoms.text('Matrix configuration modified');
    },
    sdkPluginSubtitle() {
      return atoms.heading2('SDK Configuration');
    },
    sdkPluginAdded(args: {
      pluginName: string;
    }) {
      const { pluginName } = args;
      return atoms.bulletPoint(`Configured to use the ${pluginName} plugin`);
    },
    sdkPluginRemoved(args: {
      pluginName: string;
    }) {
      const { pluginName } = args;
      return atoms.bulletPoint(`Removed the ${pluginName} plugin from the configuration`);
    },
    sdkFieldModified(args: {
      nextField: SdkConfigFieldBase;
    }) {
      const {
        nextField: {
          value, plugin, displayName,
        },
      } = args;

      // remove any periods from the end of the name (some fields have them)
      const sanitizedDisplayName = displayName.replaceAll(/\.+$/g, '');

      if (displayName === 'Dynamic Documents') {
        return atoms.bulletPoint(`${plugin} ${sanitizedDisplayName} changed`);
      }

      return atoms.bulletPoint(`${plugin} ${sanitizedDisplayName} changed to ${value}`);
    },
    failureHandlingModeSubtitle() {
      return atoms.heading2('Threshold Handling Configuration');
    },
    failureHandlingModeModified(args: {
      nextField: string;
    }) {
      const { nextField } = args;
      const modeLabel = nextField === 'full-threshold'
        ? 'fully defined parsing'
        : 'best-effort parsing';
      return atoms.bulletPoint(`Changed threshold handling mode to use ${modeLabel}`);
    },
  };
}
